import {Suspense, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {backgroundColor, flex, flexAbsoluteCenter, flexDirection, position, w100, zIndex} from "@pg-design/helpers-css";
import {borderRadius, calculateRemSize, elevation, m, p, pr} from "@pg-design/helpers-css-strings";
import {SearchIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Pill} from "@pg-design/pill-module";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {form, FormActions, IFormProps} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {IMarker, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {NAVIGATION_MOBILE_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {IFetchAllSearchListsParams} from "../../search/actions/fetch_search_all_action";
import {SearchAutocompleteWithDropdown} from "../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchTab} from "../../search/types/SearchTab";
import {generateRegionsPillLabels} from "../../search/utils/generate_regions_pill_label";
import {fetchSearchRegions} from "../../search/utils/prepare_inital_multi_search_form_values";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {defaultCircle} from "../constants/default_circle";
import {IPropertyConfiguratorSearchFiltersFormValues, propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {FiltersInfoBar} from "./FiltersInfoBar";

const pinIcon = require("../../maps/images/pins/pins_clean/primary_pin.svg");

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleChange: (name?: string, value?: unknown) => void;
    properties: IPropertyConfiguratorProperty[];
    handleOfferClick: (offerId: number | string) => void;
    onEditClick: (value: boolean) => void;
}

interface IProps extends IStateProps, IActionsProps, IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IOwnProps {}

export const MapC = (props: IProps) => {
    const hasRegionsSelected = !!props.formValues.search.regions?.length;
    const regions = props.formValues.search.regions;
    const [modalOpen, setModalOpen] = useState(false);
    const {isMobile} = useUserDevice();
    const {putPreferences} = useHomeMatchPreferencesStorage();

    const getRegions = async () => {
        try {
            const regionIds: string[] = props.formValues.search?.regions?.map((region: {id: number}) => region.id.toString()) || [];
            const newSearchValue = await fetchSearchRegions(regionIds);

            putPreferences({
                rooms: props.formValues.rooms,
                budget: props.formValues.price.upper,
                searchRegions: getHomeMatchSearchRegions(newSearchValue.search.regions)
            });
            props.formActions.update(newSearchValue);
        } catch {
            return;
        }
    };

    useEffect(() => {
        getRegions();
    }, []);

    const renderSearch = (isInModal?: boolean) => {
        return (
            <SearchAutocompleteWithDropdown
                onDropdownStatusChange={() => null}
                formProps={props}
                onAfterChange={props.handleChange}
                onSearchSubmit={() => null}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                showSelectedUnderInput={false}
                placeholder="Dodaj lokalizację"
                showArrowOnMobile={false}
                isInModal={isInModal}
            />
        );
    };

    const offerMarkers: IMarker[] = useMemo(
        () =>
            uniqBy(props.properties || [], (property) => property.offer.id).map((property) => ({
                id: property.offer.id,
                icon: {
                    url: pinIcon,
                    sizes: [24, 24]
                },
                coords: {
                    lat: property.offer.geo_point.coordinates[1],
                    lng: property.offer.geo_point.coordinates[0]
                },
                label: `${property.matching_properties_count || 1}`,
                zIndexOffset: 1000,
                isDiv: true
            })) || [],
        [props.properties]
    );

    const regionPillLabels = useMemo(() => generateRegionsPillLabels(regions, {hiddenRegionsLabelVariant: "compact"}), [regions]);

    return (
        <div css={mapWrapper}>
            {isMobile && (
                <>
                    <FiltersInfoBar {...props} />
                    <Modal
                        isOpen={modalOpen}
                        onModalClose={() => {
                            setModalOpen(false);
                        }}
                    >
                        <Modal.Header variant="bar_mini">Szukaj</Modal.Header>
                        <Modal.Content>{renderSearch(true)}</Modal.Content>
                    </Modal>
                </>
            )}
            {!isMobile && <MapTopFilters hasRegionsSelected={hasRegionsSelected}>{renderSearch()}</MapTopFilters>}
            <Suspense>
                <LazyOpenStreetMap
                    fitBounds
                    markers={offerMarkers}
                    circles={!offerMarkers.length ? defaultCircle : []}
                    onMarkerClick={(value) => props.handleOfferClick(value.marker.id)}
                    css={mapContainer}
                    labelsInsideMarkers
                >
                    {isMobile && (
                        <div
                            css={inputWrapper}
                            onClick={(event) => {
                                event.stopPropagation();
                                setModalOpen(true);
                            }}
                        >
                            <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                            {hasRegionsSelected ? (
                                <div css={inputPillsWrapper} data-testid={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                    {regionPillLabels.map((regionPillLabel, index) => (
                                        <Pill key={`${regionPillLabel}-${index.toString()}`} size="small">
                                            <Text noWrap css={[inputWrapperText]}>
                                                {regionPillLabel}
                                            </Text>
                                        </Pill>
                                    ))}
                                </div>
                            ) : (
                                <Text noWrap css={[inputWrapperText]} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                    Dodaj lokalizację
                                </Text>
                            )}
                        </div>
                    )}
                </LazyOpenStreetMap>
            </Suspense>
        </div>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

export const Map = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(MapC));

const mapWrapper = (theme: Theme) => css`
    ${flex()};
    ${position("relative")};
    ${w100};
    flex-basis: 0;
    z-index: 100;

    .leaflet-marker-icon {
        ${flexAbsoluteCenter};
        border-radius: ${calculateRemSize(3)};
        box-shadow: 0 4px 4px 0 #00000040;
        border: 1px solid ${theme.colors.secondary};
        background-color: ${theme.colors.primary};
        ${getTextVariantStyleFor("info_txt_1", false)(theme)};
        width: initial !important;
        ${p(1)};

        :hover {
            z-index: 3000 !important;
        }
    }

    @media (max-width: ${theme.breakpoints.md}) {
        flex-direction: column;
    }
`;

interface MapTopFiltersProps {
    hasRegionsSelected: boolean;
}

const MapTopFilters = styled.div<MapTopFiltersProps>`
    ${flex("center")};
    ${position("absolute")};
    ${zIndex(1)};
    top: ${calculateRemSize(2)};
    left: ${calculateRemSize(3)};
    gap: ${calculateRemSize(2)};
    width: calc(100% - ${calculateRemSize(6)});

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        flex-wrap: wrap;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        ${flexDirection("column")};
        ${position("relative")};
        flex-wrap: initial;
        top: initial;
        left: initial;
        z-index: initial;
        ${w100};
        flex-basis: 0;

        > div:first-of-type {
            margin-bottom: -${(props) => calculateRemSize(props.hasRegionsSelected ? 2 : 0)};
        }
    }

    > div {
        ${w100};
        ${m(0)};
    }

    > div:nth-of-type(2),
    > div:nth-of-type(3) {
        ${backgroundColor("white")};
        ${borderRadius(1)};
        border: 1px solid ${(props) => props.theme.colors.gray[600]};
        ${w100};
        max-width: 180px;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        > div:nth-of-type(2),
        > div:nth-of-type(3) {
            max-width: initial;
        }
    }
`;

const mapContainer = (theme: Theme) => css`
    ${position("relative")};
    ${w100};
    height: 566px;
    inset: initial;

    @media (max-width: ${theme.breakpoints.md}) {
        width: 100vw;
        align-self: center;
        flex-basis: initial;
    }
`;

const inputWrapperText = css`
    ${pr(1)};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100vw - 261px);
`;

const inputPillsWrapper = css`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: ${calculateRemSize(0.5)};
`;

const inputWrapper = (theme: Theme) => css`
    position: absolute;
    top: ${calculateRemSize(2)};
    left: ${calculateRemSize(2)};
    right: ${calculateRemSize(2)};
    display: flex;
    align-items: center;
    ${backgroundColor("white")};
    ${elevation(2)};
    border: 1px solid ${theme.colors.gray[600]};
    ${p(1, 2)};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
    z-index: 400;
`;
import {Suspense, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {backgroundColor, flex, flexAbsoluteCenter, flexDirection, position, w100, zIndex} from "@pg-design/helpers-css";
import {borderRadius, calculateRemSize, elevation, m, p, pr} from "@pg-design/helpers-css-strings";
import {SearchIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Pill} from "@pg-design/pill-module";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {form, FormActions, IFormProps} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {IMarker, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {NAVIGATION_MOBILE_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {IFetchAllSearchListsParams} from "../../search/actions/fetch_search_all_action";
import {SearchAutocompleteWithDropdown} from "../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchTab} from "../../search/types/SearchTab";
import {generateRegionsPillLabels} from "../../search/utils/generate_regions_pill_label";
import {fetchSearchRegions} from "../../search/utils/prepare_inital_multi_search_form_values";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {defaultCircle} from "../constants/default_circle";
import {IPropertyConfiguratorSearchFiltersFormValues, propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {FiltersInfoBar} from "./FiltersInfoBar";

const pinIcon = require("../../maps/images/pins/pins_clean/primary_pin.svg");

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleChange: (name?: string, value?: unknown) => void;
    properties: IPropertyConfiguratorProperty[];
    handleOfferClick: (offerId: number | string) => void;
    onEditClick: (value: boolean) => void;
}

interface IProps extends IStateProps, IActionsProps, IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IOwnProps {}

export const MapC = (props: IProps) => {
    const hasRegionsSelected = !!props.formValues.search.regions?.length;
    const regions = props.formValues.search.regions;
    const [modalOpen, setModalOpen] = useState(false);
    const {isMobile} = useUserDevice();
    const {putPreferences} = useHomeMatchPreferencesStorage();

    const getRegions = async () => {
        try {
            const regionIds: string[] = props.formValues.search?.regions?.map((region: {id: number}) => region.id.toString()) || [];
            const newSearchValue = await fetchSearchRegions(regionIds);

            putPreferences({
                rooms: props.formValues.rooms,
                budget: props.formValues.price.upper,
                searchRegions: getHomeMatchSearchRegions(newSearchValue.search.regions)
            });
            props.formActions.update(newSearchValue);
        } catch {
            return;
        }
    };

    useEffect(() => {
        getRegions();
    }, []);

    const renderSearch = (isInModal?: boolean) => {
        return (
            <SearchAutocompleteWithDropdown
                onDropdownStatusChange={() => null}
                formProps={props}
                onAfterChange={props.handleChange}
                onSearchSubmit={() => null}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                showSelectedUnderInput={false}
                placeholder="Dodaj lokalizację"
                showArrowOnMobile={false}
                isInModal={isInModal}
            />
        );
    };

    const offerMarkers: IMarker[] = useMemo(
        () =>
            uniqBy(props.properties || [], (property) => property.offer.id).map((property) => ({
                id: property.offer.id,
                icon: {
                    url: pinIcon,
                    sizes: [24, 24]
                },
                coords: {
                    lat: property.offer.geo_point.coordinates[1],
                    lng: property.offer.geo_point.coordinates[0]
                },
                label: `${property.matching_properties_count || 1}`,
                zIndexOffset: 1000,
                isDiv: true
            })) || [],
        [props.properties]
    );

    const regionPillLabels = useMemo(() => generateRegionsPillLabels(regions, {hiddenRegionsLabelVariant: "compact"}), [regions]);

    return (
        <div css={mapWrapper}>
            {isMobile && (
                <>
                    <FiltersInfoBar {...props} />
                    <Modal
                        isOpen={modalOpen}
                        onModalClose={() => {
                            setModalOpen(false);
                        }}
                    >
                        <Modal.Header variant="bar_mini">Szukaj</Modal.Header>
                        <Modal.Content>{renderSearch(true)}</Modal.Content>
                    </Modal>
                </>
            )}
            {!isMobile && <MapTopFilters hasRegionsSelected={hasRegionsSelected}>{renderSearch()}</MapTopFilters>}
            <Suspense>
                <LazyOpenStreetMap
                    fitBounds
                    markers={offerMarkers}
                    circles={!offerMarkers.length ? defaultCircle : []}
                    onMarkerClick={(value) => props.handleOfferClick(value.marker.id)}
                    css={mapContainer}
                    labelsInsideMarkers
                >
                    {isMobile && (
                        <div
                            css={inputWrapper}
                            onClick={(event) => {
                                event.stopPropagation();
                                setModalOpen(true);
                            }}
                        >
                            <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                            {hasRegionsSelected ? (
                                <div css={inputPillsWrapper} data-testid={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                    {regionPillLabels.map((regionPillLabel, index) => (
                                        <Pill key={`${regionPillLabel}-${index.toString()}`} size="small">
                                            <Text noWrap css={[inputWrapperText]}>
                                                {regionPillLabel}
                                            </Text>
                                        </Pill>
                                    ))}
                                </div>
                            ) : (
                                <Text noWrap css={[inputWrapperText]} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                    Dodaj lokalizację
                                </Text>
                            )}
                        </div>
                    )}
                </LazyOpenStreetMap>
            </Suspense>
        </div>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

export const Map = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(MapC));

const mapWrapper = (theme: Theme) => css`
    ${flex()};
    ${position("relative")};
    ${w100};
    flex-basis: 0;
    z-index: 100;

    .leaflet-marker-icon {
        ${flexAbsoluteCenter};
        border-radius: ${calculateRemSize(3)};
        box-shadow: 0 4px 4px 0 #00000040;
        border: 1px solid ${theme.colors.secondary};
        background-color: ${theme.colors.primary};
        ${getTextVariantStyleFor("info_txt_1", false)(theme)};
        width: initial !important;
        ${p(1)};

        :hover {
            z-index: 3000 !important;
        }
    }

    @media (max-width: ${theme.breakpoints.md}) {
        flex-direction: column;
    }
`;

interface MapTopFiltersProps {
    hasRegionsSelected: boolean;
}

const MapTopFilters = styled.div<MapTopFiltersProps>`
    ${flex("center")};
    ${position("absolute")};
    ${zIndex(1)};
    top: ${calculateRemSize(2)};
    left: ${calculateRemSize(3)};
    gap: ${calculateRemSize(2)};
    width: calc(100% - ${calculateRemSize(6)});

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        flex-wrap: wrap;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        ${flexDirection("column")};
        ${position("relative")};
        flex-wrap: initial;
        top: initial;
        left: initial;
        z-index: initial;
        ${w100};
        flex-basis: 0;

        > div:first-of-type {
            margin-bottom: -${(props) => calculateRemSize(props.hasRegionsSelected ? 2 : 0)};
        }
    }

    > div {
        ${w100};
        ${m(0)};
    }

    > div:nth-of-type(2),
    > div:nth-of-type(3) {
        ${backgroundColor("white")};
        ${borderRadius(1)};
        border: 1px solid ${(props) => props.theme.colors.gray[600]};
        ${w100};
        max-width: 180px;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        > div:nth-of-type(2),
        > div:nth-of-type(3) {
            max-width: initial;
        }
    }
`;

const mapContainer = (theme: Theme) => css`
    ${position("relative")};
    ${w100};
    height: 566px;
    inset: initial;

    @media (max-width: ${theme.breakpoints.md}) {
        width: 100vw;
        align-self: center;
        flex-basis: initial;
    }
`;

const inputWrapperText = css`
    ${pr(1)};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100vw - 261px);
`;

const inputPillsWrapper = css`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: ${calculateRemSize(0.5)};
`;

const inputWrapper = (theme: Theme) => css`
    position: absolute;
    top: ${calculateRemSize(2)};
    left: ${calculateRemSize(2)};
    right: ${calculateRemSize(2)};
    display: flex;
    align-items: center;
    ${backgroundColor("white")};
    ${elevation(2)};
    border: 1px solid ${theme.colors.gray[600]};
    ${p(1, 2)};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
    z-index: 400;
`;
